import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { startOfMonth, endOfMonth } from "date-fns";
import { DateField, fmtISO } from "./_shared";
import DrillDownModal from "@/components/DrillDownModal";

export function BudgetVsActualReport({ currency }) {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/budget-vs-actual", {
        params: { start: fmtISO(start), end: fmtISO(end) },
      });
      setData(data);
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="bva-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <DateField label="From" value={start} onChange={setStart} testId="bva-start" />
        <DateField label="To" value={end} onChange={setEnd} testId="bva-end" />
      </div>
      {loading && !data && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}
      {data && (
        <>
          {/* Iter 68c — honest currency note: this endpoint returns a native
              sum of budgets/expenses in the WORKSPACE DEFAULT currency and
              does not FX-consolidate. Flip the sidebar rail to filter by
              individual rails on the source Budgets + Transactions pages. */}
          <div className="rounded-md border border-[var(--divider)] bg-[#FFF9F0] px-3 py-2 text-xs text-[#7A6C4E]" data-testid="bva-currency-note">
            All figures below are a <strong>native sum in {data.currency || "USD"}</strong>. This report does not apply live FX conversion — for per-rail views, see the P&amp;L or Cash Flow reports.
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="card-flat p-4">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Total budget</div>
              <div className="font-mono-data text-lg mt-1" data-testid="bva-total-budget">{formatCurrency(data.totals.budget, data.currency || "USD")}</div>
            </div>
            <div className="card-flat p-4">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Total actual</div>
              <div className="font-mono-data text-lg mt-1" data-testid="bva-total-actual">{formatCurrency(data.totals.actual, data.currency || "USD")}</div>
            </div>
            <div className="card-flat p-4 border-l-4 border-l-[#E07A5F]">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Variance</div>
              <div className={`font-mono-data text-lg mt-1 ${data.totals.variance < 0 ? "text-[#B4283A]" : "text-[#2F6B4A]"}`} data-testid="bva-total-variance">
                {formatCurrency(data.totals.variance, data.currency || "USD")}
              </div>
            </div>
          </div>
          <div className="card-flat overflow-hidden">
            {data.rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--muted-text)]">
                No budget or expense activity in this period. Create budgets in <a className="underline" href="/budgets">Budgets</a>.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
                  <tr>
                    <th className="text-left px-5 py-2.5">Category</th>
                    <th className="text-right px-4 py-2.5">Budget</th>
                    <th className="text-right px-4 py-2.5">Actual</th>
                    <th className="text-right px-4 py-2.5">Variance</th>
                    <th className="text-right px-4 py-2.5 w-40">Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const usage = r.budget > 0 ? Math.min(200, (r.actual / r.budget) * 100) : 0;
                    return (
                      <tr key={r.category} data-testid={`bva-row-${r.category}`}
                          className="border-b border-[var(--divider)] last:border-none cursor-pointer hover:bg-[var(--hover)]"
                          title="Click to drill into the expense transactions behind this row"
                          onClick={() => setDrill({
                            title: `${r.category} · Actual ${formatCurrency(r.actual, data.currency || "USD")}`,
                            params: { kind: "expense", category: r.category,
                                      start: fmtISO(start), end: fmtISO(end) },
                          })}>
                        <td className="px-5 py-3">
                          {r.category}
                          <span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{formatCurrency(r.budget, data.currency || "USD")}</td>
                        <td className="px-4 py-3 text-right font-mono">{formatCurrency(r.actual, data.currency || "USD")}</td>
                        <td className={`px-4 py-3 text-right font-mono ${r.variance < 0 ? "text-[#B4283A]" : "text-[#2F6B4A]"}`}>{formatCurrency(r.variance, data.currency || "USD")}</td>
                        <td className="px-4 py-3">
                          {r.budget > 0 ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-[var(--hover)] rounded-full overflow-hidden">
                                <div style={{ width: `${Math.min(100, usage)}%` }} className={`h-full ${usage > 100 ? "bg-[#B4283A]" : usage > 80 ? "bg-[#D5A02A]" : "bg-[#2F6B4A]"}`} />
                              </div>
                              <span className="text-xs text-[var(--muted-text)] w-10 text-right">{usage.toFixed(0)}%</span>
                            </div>
                          ) : <span className="text-xs text-[var(--muted-text)]">no budget</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={data?.currency || currency}
      />
    </div>
  );
}
